import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_TONE = {
  completed: { bg: 'rgba(58,74,92,.10)', color: '#3A4A5C', label: 'Replay' },
  live_today: { bg: '#D63031', color: '#FFFFFF', label: '● Live Today' },
  upcoming: { bg: '#C8A84B', color: '#0D1B2A', label: 'Upcoming' },
  scheduled: { bg: 'transparent', color: '#3A4A5C', label: 'Scheduled' },
};

export default function WebinarsPage() {
  const [data, setData] = useState(null);
  const [openWebinar, setOpenWebinar] = useState(null);
  const [calendarMenuFor, setCalendarMenuFor] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [success, setSuccess] = useState(null);
  const [err, setErr] = useState('');
  const [form, setForm] = useState({
    full_name: '', email: '', role: 'audience', title: '', organisation: '', country: '', linkedin_url: '', notes: '',
  });

  useEffect(() => {
    document.title = '2026 Webinar Series · SKI AXIS';
    axios.get(`${API}/webinars`).then(r => setData(r.data)).catch(() => {});
  }, []);

  const calLinks = (slug) => ({
    google: `${API}/webinars/${slug}/calendar/google`,
    outlook: `${API}/webinars/${slug}/calendar/outlook`,
    ics: `${API}/webinars/${slug}/calendar.ics`,
  });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!openWebinar) return;
    setErr(''); setRegistering(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach(k => { if (payload[k] === '') delete payload[k]; });

      // Audience tier requires AED 149 payment via on-platform Stripe Checkout
      if (form.role === 'audience') {
        // Step 1: capture the registration in our DB (status: pending until paid)
        await axios.post(`${API}/webinars/${openWebinar.slug}/register`, payload).catch(() => {});
        // Step 2: create Stripe Checkout session and redirect
        const urlParams = new URLSearchParams(window.location.search);
        const testMode = urlParams.get('test') === '1' || localStorage.getItem('stripe_test_mode') === '1';
        const endpoint = `${API}/payments/checkout/webinar${testMode ? '?test=1' : ''}`;
        const { data } = await axios.post(endpoint, {
          origin_url: window.location.origin,
          email: form.email,
          full_name: form.full_name,
          webinar_slug: openWebinar.slug,
        });
        if (data?.url) {
          window.location.href = data.url;
          return;
        }
        throw new Error('Could not start payment.');
      }

      // Speaker tier (paid · AED 500 · June 2026 onwards) — only if slots are open
      if (form.role === 'speaker' && openWebinar.speaker_tier_paid && openWebinar.speaker_slots_open_now) {
        const urlParams = new URLSearchParams(window.location.search);
        const testMode = urlParams.get('test') === '1' || localStorage.getItem('stripe_test_mode') === '1';
        const endpoint = `${API}/payments/checkout/webinar-speaker${testMode ? '?test=1' : ''}`;
        const { data } = await axios.post(endpoint, {
          origin_url: window.location.origin,
          email: form.email,
          full_name: form.full_name,
          webinar_slug: openWebinar.slug,
          organisation: form.organisation,
          talk_title: form.title,
          talk_abstract: form.notes,
          linkedin_url: form.linkedin_url,
        });
        if (data?.url) {
          window.location.href = data.url;
          return;
        }
        throw new Error('Could not start speaker payment.');
      }

      // Free tiers (Speaker / Delegate / Ministry / Media / Corporate) — direct register
      const { data: resp } = await axios.post(`${API}/webinars/${openWebinar.slug}/register`, payload);
      setSuccess({ webinar: openWebinar, ...resp });
      setOpenWebinar(null);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === 'string' ? detail : 'Registration failed. Please check your details.');
    } finally {
      setRegistering(false);
    }
  };

  // Off-platform Stripe Payment Link (fallback for audience tier — quicker, no integration)
  const openPaymentLinkFallback = () => {
    if (!form.email || !form.full_name) {
      setErr('Please enter your name and email first — even for the quick-pay link.');
      return;
    }
    // Capture the registration in our DB so it shows as "pending payment" in admin
    axios.post(`${API}/webinars/${openWebinar.slug}/register`, { ...form }).catch(() => {});
    window.open('https://buy.stripe.com/7sYaERd4sd0931O1HO1ck00', '_blank', 'noopener,noreferrer');
  };

  const next = data?.next;
  const upcoming = useMemo(() => (data?.webinars || []).filter(w => w.status !== 'completed'), [data]);
  const past = useMemo(() => (data?.webinars || []).filter(w => w.status === 'completed'), [data]);

  return (
    <div className="wb-root" data-testid="webinars-page" style={{ minHeight: '100vh', background: '#FAF7F0', color: '#0D1B2A', fontFamily: 'Georgia, "Times New Roman", serif' }}>
      <style>{`
        .wb-root { --gold:#C8A84B; --gold-line:rgba(200,168,75,.35); --ink:#0D1B2A; --mist:#3A4A5C; }
        .wb-nav { display:flex; align-items:center; justify-content:space-between; padding:24px 48px; max-width:1380px; margin:0 auto; }
        .wb-logo { font-family:'Cormorant Garamond',Georgia,serif; font-size:22px; font-weight:700; letter-spacing:-.01em; text-decoration:none; color:#0D1B2A; cursor:pointer; }
        .wb-logo em { color:#C8A84B; font-style:italic; }
        .wb-back { font-size:11px; letter-spacing:.18em; text-transform:uppercase; color:#3A4A5C; text-decoration:none; }
        .wb-hero { max-width:1380px; margin:0 auto; padding:48px 48px 24px; }
        .wb-eyebrow { font-size:11px; font-weight:700; letter-spacing:.32em; color:#C8A84B; text-transform:uppercase; margin-bottom:18px; display:flex; align-items:center; gap:14px; }
        .wb-eyebrow::before { content:''; display:block; width:32px; height:1px; background:#C8A84B; }
        .wb-title { font-family:'Cormorant Garamond',Georgia,serif; font-size:60px; line-height:1.04; font-weight:700; letter-spacing:-.022em; margin:0 0 22px; max-width:980px; }
        .wb-title em { color:#C8A84B; font-style:italic; }
        .wb-sub { font-size:17px; line-height:1.7; color:#3A4A5C; max-width:820px; margin:0 0 24px; }
        @media (max-width:760px) { .wb-nav { padding:18px 22px; } .wb-hero { padding:32px 22px 18px; } .wb-title { font-size:36px; } .wb-sub { font-size:15px; } }

        .wb-cadence { display:inline-block; background:#0D1B2A; color:#C8A84B; padding:8px 16px; font-size:10.5px; font-weight:700; letter-spacing:.24em; text-transform:uppercase; }

        /* NEXT FEATURED */
        .wb-next { max-width:1380px; margin:0 auto; padding:24px 48px; }
        .wb-next-card { background:linear-gradient(155deg,#0D1B2A 0%,#1A2F4A 100%); color:#FAF7F0; padding:48px 52px; border:1px solid rgba(200,168,75,.4); position:relative; overflow:hidden; }
        .wb-next-tier { font-size:10.5px; font-weight:700; letter-spacing:.32em; color:#C8A84B; text-transform:uppercase; margin-bottom:18px; }
        .wb-next-grid { display:grid; grid-template-columns:160px 1fr auto; gap:36px; align-items:center; }
        @media (max-width:880px) { .wb-next { padding:16px 22px; } .wb-next-card { padding:30px 26px; } .wb-next-grid { grid-template-columns:1fr; gap:18px; } }
        .wb-date-block { text-align:center; padding:18px 14px; border:1px solid rgba(200,168,75,.4); }
        .wb-date-month { font-size:10.5px; font-weight:700; letter-spacing:.24em; color:#C8A84B; text-transform:uppercase; }
        .wb-date-day { font-family:'Cormorant Garamond',Georgia,serif; font-size:64px; line-height:1; font-weight:700; letter-spacing:-.025em; margin:6px 0; }
        .wb-date-weekday { font-size:11px; letter-spacing:.16em; color:rgba(250,247,240,.65); text-transform:uppercase; }
        .wb-next-title { font-family:'Cormorant Garamond',Georgia,serif; font-size:34px; line-height:1.15; font-weight:700; letter-spacing:-.012em; margin:0 0 10px; }
        .wb-next-title em { color:#C8A84B; font-style:italic; }
        .wb-next-meta { font-size:13.5px; opacity:.8; margin:0 0 14px; line-height:1.6; }
        .wb-next-summary { font-size:15px; line-height:1.7; color:rgba(250,247,240,.85); margin:0 0 20px; max-width:560px; }
        .wb-cta-gold { display:inline-block; background:#C8A84B; color:#0D1B2A; padding:14px 26px; font-size:11px; font-weight:700; letter-spacing:.18em; text-transform:uppercase; text-decoration:none; cursor:pointer; border:none; font-family:Georgia,serif; transition:transform .25s ease, background .25s ease; }
        .wb-cta-gold:hover { transform:translateY(-2px); background:#D4B65C; }
        .wb-cta-line { display:inline-block; background:transparent; color:#FAF7F0; border:1px solid #C8A84B; padding:14px 26px; font-size:11px; font-weight:700; letter-spacing:.18em; text-transform:uppercase; text-decoration:none; cursor:pointer; font-family:Georgia,serif; transition:background .2s ease; margin-left:10px; }
        .wb-cta-line:hover { background:rgba(200,168,75,.12); }

        /* CALENDAR GRID */
        .wb-section { max-width:1380px; margin:48px auto 0; padding:0 48px; }
        @media (max-width:760px) { .wb-section { padding:0 22px; } }
        .wb-section-title { font-family:'Cormorant Garamond',Georgia,serif; font-size:32px; font-weight:700; letter-spacing:-.012em; margin:0 0 6px; }
        .wb-section-sub { font-size:13.5px; color:#3A4A5C; margin:0 0 28px; font-style:italic; }
        .wb-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(320px, 1fr)); gap:20px; padding-bottom:32px; }

        .wb-card { background:#FFFFFF; border:1px solid var(--gold-line); padding:28px 28px 24px; transition:transform .3s ease, border-color .3s ease, box-shadow .3s ease; position:relative; }
        .wb-card:hover { transform:translateY(-3px); border-color:#C8A84B; box-shadow:0 12px 40px -22px rgba(13,27,42,.32); }
        .wb-card-header { display:flex; gap:18px; align-items:flex-start; margin-bottom:16px; }
        .wb-card-date { width:64px; flex-shrink:0; text-align:center; padding:10px 6px; background:#FAF7F0; border-left:3px solid #C8A84B; }
        .wb-card-date-m { font-size:9.5px; font-weight:700; letter-spacing:.18em; color:#C8A84B; text-transform:uppercase; }
        .wb-card-date-d { font-family:'Cormorant Garamond',Georgia,serif; font-size:32px; line-height:1; font-weight:700; margin:4px 0 2px; }
        .wb-card-date-w { font-size:9.5px; letter-spacing:.1em; color:#3A4A5C; text-transform:uppercase; }
        .wb-card-status-pill { display:inline-block; padding:4px 10px; font-size:9.5px; font-weight:700; letter-spacing:.18em; text-transform:uppercase; }
        .wb-card-track { font-size:10.5px; font-weight:700; letter-spacing:.18em; color:#C8A84B; text-transform:uppercase; margin:8px 0 8px; }
        .wb-card-title { font-family:'Cormorant Garamond',Georgia,serif; font-size:22px; line-height:1.2; font-weight:700; letter-spacing:-.005em; margin:0 0 8px; }
        .wb-card-summary { font-size:13px; line-height:1.55; color:#3A4A5C; margin:0 0 14px; }
        .wb-card-host { font-size:12px; color:#7A8190; font-style:italic; margin:0 0 16px; }
        .wb-card-host strong { color:#0D1B2A; font-style:normal; font-weight:600; }
        .wb-card-actions { display:flex; gap:10px; align-items:center; margin-top:6px; flex-wrap:wrap; }
        .wb-btn-mini { display:inline-block; padding:8px 14px; font-size:10px; font-weight:700; letter-spacing:.14em; text-transform:uppercase; text-decoration:none; cursor:pointer; border:1px solid var(--gold-line); background:transparent; color:#0D1B2A; font-family:Georgia,serif; transition:all .2s ease; }
        .wb-btn-mini:hover { background:#0D1B2A; color:#C8A84B; border-color:#0D1B2A; }
        .wb-btn-cta { background:#0D1B2A; color:#C8A84B; border-color:#0D1B2A; }

        .wb-cal-menu { position:absolute; top:100%; right:0; margin-top:4px; background:#FFFFFF; border:1px solid var(--gold-line); padding:6px 0; min-width:160px; box-shadow:0 10px 30px -12px rgba(13,27,42,.28); z-index:50; }
        .wb-cal-menu a { display:block; padding:10px 16px; font-size:12px; color:#0D1B2A; text-decoration:none; transition:background .15s ease; }
        .wb-cal-menu a:hover { background:#FAF7F0; color:#C8A84B; }

        /* MODAL */
        .wb-modal-back { position:fixed; inset:0; background:rgba(13,27,42,.72); backdrop-filter:blur(8px); display:flex; align-items:center; justify-content:center; padding:20px; z-index:200; animation:wbfadein .25s ease; }
        @keyframes wbfadein { from { opacity:0; } to { opacity:1; } }
        .wb-modal { background:#FFFFFF; max-width:600px; width:100%; padding:38px 38px 32px; border:1px solid var(--gold-line); max-height:92vh; overflow-y:auto; }
        @media (max-width:560px) { .wb-modal { padding:26px 22px 22px; } }
        .wb-modal-tier { font-size:10.5px; font-weight:700; letter-spacing:.32em; color:#C8A84B; text-transform:uppercase; margin-bottom:8px; }
        .wb-modal-title { font-family:'Cormorant Garamond',Georgia,serif; font-size:26px; line-height:1.18; font-weight:700; letter-spacing:-.008em; margin:0 0 16px; }
        .wb-modal-title em { color:#C8A84B; font-style:italic; }
        .wb-roles { display:grid; grid-template-columns:1fr 1fr; gap:8px; margin:0 0 18px; }
        @media (max-width:480px) { .wb-roles { grid-template-columns:1fr; } }
        .wb-role { padding:10px 12px; border:1px solid var(--gold-line); cursor:pointer; transition:border-color .15s ease, background .15s ease; }
        .wb-role.active { border-color:#C8A84B; background:#FAF7F0; }
        .wb-role-name { font-size:12.5px; font-weight:700; color:#0D1B2A; margin-bottom:2px; }
        .wb-role-desc { font-size:11px; color:#3A4A5C; line-height:1.45; }
        .wb-field { display:block; margin-bottom:14px; }
        .wb-field-lbl { font-size:11px; font-weight:700; letter-spacing:.14em; text-transform:uppercase; color:#3A4A5C; margin-bottom:6px; display:block; }
        .wb-field input, .wb-field textarea { width:100%; padding:11px 14px; font-size:14px; font-family:Georgia,serif; color:#0D1B2A; background:#FAF7F0; border:1px solid rgba(13,27,42,.18); outline:none; transition:border-color .2s ease; box-sizing:border-box; border-radius:0; }
        .wb-field input:focus, .wb-field textarea:focus { border-color:#C8A84B; }
        .wb-row { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
        @media (max-width:480px) { .wb-row { grid-template-columns:1fr; } }
        .wb-modal-actions { display:flex; gap:10px; align-items:center; margin-top:6px; }
        .wb-cancel { background:transparent; border:none; font-size:11px; letter-spacing:.18em; text-transform:uppercase; color:#3A4A5C; cursor:pointer; padding:14px 16px; font-weight:700; font-family:Georgia,serif; }
        .wb-err { background:rgba(214,48,49,.08); border-left:3px solid #D63031; color:#D63031; padding:10px 14px; font-size:13px; margin-bottom:14px; }

        /* AGENDA */
        .wb-agenda { margin:18px 0 6px; padding:18px 20px; background:#FBF8EE; border:1px solid var(--gold-line); }
        .wb-agenda-h { font-size:10.5px; font-weight:700; letter-spacing:.32em; color:#8A6A14; text-transform:uppercase; margin-bottom:12px; }
        .wb-agenda ul { list-style:none; padding:0; margin:0; }
        .wb-agenda li { display:grid; grid-template-columns:120px 1fr; gap:14px; padding:10px 0; border-top:1px dashed rgba(13,27,42,.10); }
        .wb-agenda li:first-child { border-top:none; }
        .wb-agenda-time { font-size:11.5px; font-weight:700; letter-spacing:.06em; color:#8A6A14; padding-top:2px; }
        .wb-agenda-body strong { display:inline; font-family:Cormorant Garamond,Georgia,serif; font-size:15px; color:#0D1B2A; font-weight:700; }
        .wb-agenda-who { font-size:13px; color:#3A4A5C; font-style:italic; }
        .wb-agenda-what { font-size:12.5px; color:#3A4A5C; margin:4px 0 0; line-height:1.55; }
        @media (max-width:480px) { .wb-agenda li { grid-template-columns:1fr; gap:2px; } .wb-agenda-time { font-size:10.5px; } }

        /* SUCCESS */
        .wb-success { max-width:720px; margin:0 auto; background:#FFFFFF; border:1px solid var(--gold-line); padding:48px 44px; text-align:center; }

        /* SKELETON (loading state) */
        .wb-sk-card { background:#FFFFFF; border:1px solid var(--gold-line); padding:28px 28px 24px; position:relative; overflow:hidden; }
        .wb-sk-card::after { content:''; position:absolute; inset:0; background:linear-gradient(90deg, transparent 0%, rgba(200,168,75,.08) 50%, transparent 100%); animation:wb-sk-shimmer 1.6s ease-in-out infinite; }
        .wb-sk-row { background:rgba(13,27,42,.06); height:14px; border-radius:2px; margin-bottom:10px; }
        .wb-sk-date-block { width:64px; height:72px; background:rgba(200,168,75,.10); border-left:3px solid rgba(200,168,75,.4); float:left; margin-right:18px; }
        .wb-sk-pill { display:inline-block; width:96px; height:18px; background:rgba(200,168,75,.16); margin-bottom:10px; }
        .wb-sk-title { height:24px; background:rgba(13,27,42,.10); margin:14px 0 10px; width:88%; }
        .wb-sk-line { height:11px; background:rgba(13,27,42,.06); margin-bottom:7px; }
        .wb-sk-line.short { width:62%; }
        .wb-sk-line.medium { width:80%; }
        .wb-sk-actions { display:flex; gap:10px; margin-top:18px; }
        .wb-sk-btn { width:120px; height:32px; background:rgba(13,27,42,.08); }
        .wb-sk-btn.gold { background:rgba(200,168,75,.18); }
        @keyframes wb-sk-shimmer { 0% { transform:translateX(-100%); } 100% { transform:translateX(100%); } }
      `}</style>

      <div className="wb-nav">
        <Link to="/" className="wb-logo" data-testid="webinars-back-home"><strong>SKI</strong> <em>AXIS</em></Link>
        <Link to="/" className="wb-back" data-testid="webinars-back">← Back</Link>
      </div>

      {/* HERO */}
      <section className="wb-hero">
        <div className="wb-eyebrow">SKI Axis · Monthly Webinar Series · 2026</div>
        <h1 className="wb-title">A live institutional briefing, the <em>last Saturday</em> of every month.</h1>
        <p className="wb-sub">
          Twelve 90-minute live sessions across 2026 — designed to feed founders, investors, ministry partners and corporate sponsors into the SKI Axis growth flywheel.
          Every webinar connects to the Cohort 1 accelerator and the Invest UAE Forum 2026.
          Six participation tiers · One-click calendar sync · Full automation.
        </p>
        <div className="wb-cadence">📅 Every last Saturday · 6:00 PM BST · Live online</div>
      </section>

      {success && (
        <section className="wb-section">
          <div className="wb-success" data-testid="webinar-success">
            <div className="wb-modal-tier">{success.status === 'pending' ? 'Application Received' : 'You\'re Confirmed'}</div>
            <h2 style={{ fontFamily: '"Cormorant Garamond",serif', fontSize: 36, fontWeight: 700, margin: '0 0 12px' }}>{success.webinar?.title}</h2>
            <p style={{ fontSize: 14, color: '#3A4A5C', margin: '0 0 24px', lineHeight: 1.6 }}>{success.message}</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href={success.calendar_links?.google} target="_blank" rel="noopener noreferrer" className="wb-btn-mini wb-btn-cta" data-testid="success-cal-google">Add to Google →</a>
              <a href={success.calendar_links?.outlook} target="_blank" rel="noopener noreferrer" className="wb-btn-mini" data-testid="success-cal-outlook">Outlook →</a>
              <a href={success.calendar_links?.ics} className="wb-btn-mini" data-testid="success-cal-ics">Apple / .ics →</a>
              <button onClick={() => { setSuccess(null); setForm({ full_name: '', email: '', role: 'audience', title: '', organisation: '', country: '', linkedin_url: '', notes: '' }); }} className="wb-cancel" data-testid="success-back">← Back to calendar</button>
            </div>
          </div>
        </section>
      )}

      {!success && next && (
        <section className="wb-next">
          <div className="wb-next-card">
            <div className="wb-next-tier">★ Next live · {next.status === 'live_today' ? 'Today' : 'Coming up'}</div>
            <div className="wb-next-grid">
              <div className="wb-date-block">
                <div className="wb-date-month">{next.month_short}</div>
                <div className="wb-date-day">{next.day}</div>
                <div className="wb-date-weekday">{next.weekday}</div>
              </div>
              <div>
                <div className="wb-next-title"><em>{next.title.split(':')[0]}</em>{next.title.includes(':') ? ': ' + next.title.split(':').slice(1).join(':').trim() : ''}</div>
                <div className="wb-next-meta">{next.track} · {next.time_label} · Host: {next.host}</div>
                <p className="wb-next-summary">{next.summary}</p>
              </div>
              <div style={{ textAlign: 'right' }}>
                {next.register_url ? (
                  <>
                    <a href={next.register_url} target="_blank" rel="noopener noreferrer" className="wb-cta-gold" data-testid="webinar-next-eventbrite" style={{ marginBottom: 6 }}>Reserve · Eventbrite →</a>
                    <button className="wb-cta-line" onClick={() => setOpenWebinar(next)} data-testid="webinar-next-register-app" style={{ display: 'block', marginBottom: 6 }}>Register in App →</button>
                  </>
                ) : (
                  <button className="wb-cta-gold" onClick={() => setOpenWebinar(next)} data-testid="webinar-next-register">Register →</button>
                )}
                <a href={calLinks(next.slug).ics} className="wb-cta-line" data-testid="webinar-next-cal">+ Calendar</a>
              </div>
            </div>
          </div>
        </section>
      )}

      {!success && (
        <section className="wb-section">
          <h2 className="wb-section-title">2026 Annual Calendar</h2>
          <div className="wb-section-sub">{data ? `${data.count} live sessions · last Saturday cadence · all auto-synced to your calendar` : 'Loading the calendar…'}</div>
          {!data && (
            <div className="wb-grid" data-testid="webinar-skeleton-grid">
              {[0,1,2,3,4,5].map((i) => (
                <article key={`sk-${i}`} className="wb-sk-card" data-testid={`webinar-skeleton-${i}`}>
                  <div className="wb-card-header">
                    <div className="wb-sk-date-block" />
                    <div style={{ flex:1 }}>
                      <span className="wb-sk-pill" />
                      <div className="wb-sk-line short" style={{ marginTop:8 }} />
                    </div>
                  </div>
                  <div className="wb-sk-title" />
                  <div className="wb-sk-line medium" />
                  <div className="wb-sk-line" />
                  <div className="wb-sk-line short" style={{ marginBottom:16 }} />
                  <div className="wb-sk-actions">
                    <div className="wb-sk-btn gold" />
                    <div className="wb-sk-btn" />
                  </div>
                </article>
              ))}
            </div>
          )}
          <div className="wb-grid">
            {upcoming.map((w) => (
              <article key={w.slug} className="wb-card" data-testid={`webinar-card-${w.slug}`}>
                <div className="wb-card-header">
                  <div className="wb-card-date">
                    <div className="wb-card-date-m">{w.month_short}</div>
                    <div className="wb-card-date-d">{w.day}</div>
                    <div className="wb-card-date-w">{w.weekday.slice(0,3)}</div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <span className="wb-card-status-pill" style={{ background: STATUS_TONE[w.status]?.bg, color: STATUS_TONE[w.status]?.color, border: w.status === 'scheduled' ? '1px solid var(--gold-line)' : 'none' }}>{STATUS_TONE[w.status]?.label}</span>
                    <div className="wb-card-track">{w.track}</div>
                  </div>
                </div>
                <h3 className="wb-card-title">{w.title}</h3>
                <p className="wb-card-summary">{w.summary}</p>
                <p className="wb-card-host"><strong>{w.host}</strong>{w.host_role ? <> · <span style={{ color: '#3A4A5C' }}>{w.host_role}</span></> : null} · {w.time_label}</p>
                <div className="wb-card-actions">
                  <button className="wb-btn-mini wb-btn-cta" onClick={() => setOpenWebinar(w)} data-testid={`webinar-register-${w.slug}`}>Register →</button>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <button className="wb-btn-mini" onClick={(e) => { e.stopPropagation(); setCalendarMenuFor(calendarMenuFor === w.slug ? null : w.slug); }} data-testid={`webinar-cal-${w.slug}`}>+ Add to Calendar ▾</button>
                    {calendarMenuFor === w.slug && (
                      <div className="wb-cal-menu" onClick={(e) => e.stopPropagation()}>
                        <a href={calLinks(w.slug).google} target="_blank" rel="noopener noreferrer">Google Calendar →</a>
                        <a href={calLinks(w.slug).outlook} target="_blank" rel="noopener noreferrer">Outlook Live →</a>
                        <a href={calLinks(w.slug).ics}>Apple / .ics →</a>
                      </div>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>

          {past.length > 0 && (
            <>
              <h2 className="wb-section-title" style={{ marginTop: 56 }}>Earlier in 2026 · Replays</h2>
              <div className="wb-section-sub">{past.length} session{past.length === 1 ? '' : 's'} · recordings available to community members</div>
              <div className="wb-grid">
                {past.map((w) => (
                  <article key={w.slug} className="wb-card" style={{ opacity: .82 }} data-testid={`webinar-card-${w.slug}`}>
                    <div className="wb-card-header">
                      <div className="wb-card-date" style={{ background: 'rgba(58,74,92,.06)', borderLeftColor: '#3A4A5C' }}>
                        <div className="wb-card-date-m" style={{ color: '#3A4A5C' }}>{w.month_short}</div>
                        <div className="wb-card-date-d">{w.day}</div>
                        <div className="wb-card-date-w">{w.weekday.slice(0,3)}</div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <span className="wb-card-status-pill" style={{ background: STATUS_TONE.completed.bg, color: STATUS_TONE.completed.color }}>{STATUS_TONE.completed.label}</span>
                        <div className="wb-card-track">{w.track}</div>
                      </div>
                    </div>
                    <h3 className="wb-card-title">{w.title}</h3>
                    <p className="wb-card-summary">{w.summary}</p>
                    <p className="wb-card-host"><strong>{w.host}</strong></p>
                    <div className="wb-card-actions">
                      <Link to="/community" className="wb-btn-mini" data-testid={`webinar-replay-${w.slug}`}>Join community for replay →</Link>
                    </div>
                  </article>
                ))}
              </div>
            </>
          )}
        </section>
      )}

      {/* MODAL */}
      {openWebinar && (
        <div className="wb-modal-back" onClick={() => setOpenWebinar(null)} data-testid="webinar-modal-backdrop">
          <div className="wb-modal" onClick={(e) => e.stopPropagation()} data-testid="webinar-modal">
            <div className="wb-modal-tier">{openWebinar.month} {openWebinar.day} · {openWebinar.weekday}</div>
            <h3 className="wb-modal-title">Register · <em>{openWebinar.title}</em></h3>

            {/* AGENDA — full run of show */}
            {openWebinar.agenda && openWebinar.agenda.length > 0 && (
              <div className="wb-agenda" data-testid="webinar-modal-agenda">
                <div className="wb-agenda-h">Run of show · 90-min live broadcast</div>
                <ul>
                  {openWebinar.agenda.map((seg, i) => (
                    <li key={i}>
                      <span className="wb-agenda-time">{seg.time}</span>
                      <div className="wb-agenda-body">
                        <strong>{seg.label}</strong>
                        {seg.who ? <span className="wb-agenda-who"> · {seg.who}</span> : null}
                        {seg.what ? <p className="wb-agenda-what">{seg.what}</p> : null}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* SPEAKER SLOTS STATUS BANNER — only for paid-tier webinars (Jun 2026+) */}
            {openWebinar.speaker_tier_paid && (
              <div
                className="wb-speaker-banner"
                data-testid="webinar-speaker-banner"
                style={{
                  background: openWebinar.speaker_slots_open_now ? 'rgba(200,168,75,.12)' : 'rgba(58,74,92,.08)',
                  borderLeft: `3px solid ${openWebinar.speaker_slots_open_now ? '#C8A84B' : '#3A4A5C'}`,
                  padding: '14px 16px',
                  margin: '20px 0 8px',
                  fontSize: 13,
                  lineHeight: 1.55,
                  color: '#0D1B2A',
                }}
              >
                <strong style={{ display: 'block', fontSize: 11, letterSpacing: '.18em', textTransform: 'uppercase', color: '#8A6A14', marginBottom: 6 }}>
                  Speaker Slots · AED 500
                </strong>
                {openWebinar.speaker_slots_open_now ? (
                  <>Speaker registrations are <strong>open now</strong>. Reserve a 10-minute talk slot below — faculty review within 48h, full share kit + recording rights included.</>
                ) : (
                  <>Speaker slots open exactly <strong>25 days before</strong> the webinar. Currently <strong>opens in {openWebinar.pricing?.speaker?.days_until_open ?? '—'} days</strong> ({openWebinar.pricing?.speaker?.slots_open_at}). Mark your calendar.</>
                )}
              </div>
            )}
            {!openWebinar.speaker_tier_paid && (
              <div
                className="wb-speaker-banner"
                style={{ background: 'rgba(58,74,92,.06)', borderLeft: '3px solid #3A4A5C', padding: '14px 16px', margin: '20px 0 8px', fontSize: 13, color: '#3A4A5C' }}
              >
                <strong style={{ display: 'block', fontSize: 11, letterSpacing: '.18em', textTransform: 'uppercase', color: '#3A4A5C', marginBottom: 6 }}>
                  Speakers · By invitation
                </strong>
                Pre-June 2026 webinars were curated by faculty. The public AED 500 speaker tier launches with the <strong>June</strong> webinar.
              </div>
            )}

            {err && <div className="wb-err" data-testid="webinar-form-err">{err}</div>}

            <div className="wb-field-lbl">Choose your participation tier</div>
            <div className="wb-roles">
              {data?.roles && Object.entries(data.roles).map(([key, meta]) => {
                // Show price hint per role
                const priceHint =
                  key === 'speaker' && openWebinar.speaker_tier_paid ? `AED 500 · ${openWebinar.speaker_slots_open_now ? 'Slots open' : `Opens in ${openWebinar.pricing?.speaker?.days_until_open ?? '—'}d`}`
                  : key === 'speaker' ? 'By invitation'
                  : key === 'audience' || key === 'delegate' ? 'AED 149'
                  : '';
                return (
                  <div key={key} className={`wb-role ${form.role === key ? 'active' : ''}`} onClick={() => setForm({ ...form, role: key })} data-testid={`webinar-role-${key}`}>
                    <div className="wb-role-name">{meta.icon} {meta.label}{priceHint && <span style={{ float: 'right', fontSize: 11, color: '#8A6A14', fontWeight: 700, letterSpacing: '.06em' }}>{priceHint}</span>}</div>
                    <div className="wb-role-desc">{meta.description}</div>
                  </div>
                );
              })}
            </div>

            <form onSubmit={onSubmit}>
              <div className="wb-row">
                <label className="wb-field"><span className="wb-field-lbl">Full Name *</span><input type="text" name="full_name" required value={form.full_name} onChange={onChange} data-testid="webinar-input-name" /></label>
                <label className="wb-field"><span className="wb-field-lbl">Email *</span><input type="email" name="email" required value={form.email} onChange={onChange} data-testid="webinar-input-email" /></label>
              </div>
              <div className="wb-row">
                <label className="wb-field"><span className="wb-field-lbl">{form.role === 'speaker' ? 'Talk title *' : 'Title'}</span><input type="text" name="title" placeholder={form.role === 'speaker' ? 'Your proposed talk title' : 'e.g. Founder, Partner, Director'} value={form.title} onChange={onChange} data-testid="webinar-input-title" /></label>
                <label className="wb-field"><span className="wb-field-lbl">Organisation</span><input type="text" name="organisation" value={form.organisation} onChange={onChange} data-testid="webinar-input-org" /></label>
              </div>
              <div className="wb-row">
                <label className="wb-field"><span className="wb-field-lbl">Country</span><input type="text" name="country" value={form.country} onChange={onChange} data-testid="webinar-input-country" /></label>
                <label className="wb-field"><span className="wb-field-lbl">LinkedIn</span><input type="url" name="linkedin_url" placeholder="https://linkedin.com/in/..." value={form.linkedin_url} onChange={onChange} data-testid="webinar-input-linkedin" /></label>
              </div>
              <label className="wb-field">
                <span className="wb-field-lbl">{form.role === 'speaker' ? 'Talk abstract (3–4 lines)' : form.role === 'corporate_partner' ? 'Sponsorship interest / brand goal' : 'Notes / what you\'re looking for'}</span>
                <textarea name="notes" rows={3} maxLength={400} value={form.notes} onChange={onChange} data-testid="webinar-input-notes" />
              </label>
              <div className="wb-modal-actions">
                <button
                  type="submit"
                  disabled={registering || (form.role === 'speaker' && openWebinar.speaker_tier_paid && !openWebinar.speaker_slots_open_now)}
                  className="wb-cta-gold"
                  data-testid="webinar-submit"
                >
                  {registering
                    ? 'Submitting…'
                    : form.role === 'audience'
                      ? 'Pay AED 149 · Stripe Checkout →'
                      : form.role === 'speaker' && openWebinar.speaker_tier_paid && openWebinar.speaker_slots_open_now
                        ? 'Pay AED 500 · Reserve Speaker Slot →'
                        : form.role === 'speaker' && openWebinar.speaker_tier_paid
                          ? `Slots open in ${openWebinar.pricing?.speaker?.days_until_open ?? '—'} days`
                          : form.role === 'speaker' || form.role === 'corporate_partner'
                            ? 'Submit Application →'
                            : 'Confirm Registration →'}
                </button>
                {form.role === 'audience' && (
                  <button type="button" onClick={openPaymentLinkFallback} disabled={registering} data-testid="webinar-payment-link" style={{ background: 'transparent', color: '#0D1B2A', border: '1px solid rgba(13,27,42,.3)', padding: '14px 22px', fontSize: 11, fontWeight: 700, letterSpacing: '.16em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'Georgia,serif', whiteSpace: 'nowrap' }}>
                    Quick Pay Link →
                  </button>
                )}
                <button type="button" onClick={() => setOpenWebinar(null)} className="wb-cancel" data-testid="webinar-modal-cancel">Cancel</button>
              </div>
              {form.role === 'audience' && (
                <div style={{ marginTop: 12, fontSize: 12, color: '#3A4A5C', fontStyle: 'italic', lineHeight: 1.5 }}>
                  <strong style={{ color: '#0D1B2A', fontStyle: 'normal' }}>Two ways to pay AED 149:</strong> the on-platform <em>Stripe Checkout</em> ties your payment to this webinar, fires your branded confirmation + ICS calendar invite, and adds you to our records — recommended. The <em>Quick Pay Link</em> opens Stripe directly and is faster but won't auto-link to this webinar.
                </div>
              )}
              {form.role === 'speaker' && openWebinar.speaker_tier_paid && (
                <div style={{ marginTop: 12, fontSize: 12, color: '#3A4A5C', fontStyle: 'italic', lineHeight: 1.55 }}>
                  <strong style={{ color: '#0D1B2A', fontStyle: 'normal' }}>What AED 500 includes:</strong> a 10-minute live talk slot · faculty review &amp; coaching call · OG share card &amp; speaker page on skiaxis.com · social share kit (LinkedIn / WhatsApp / X copy) · full edited recording within 7 days · long-term faculty status with SKI Axis.
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
